import { useState, useEffect } from "react";
import { CustomMemojiCreator } from "@/components/CustomMemojiCreator";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { User } from "@supabase/supabase-js";
import { RefreshCw, Check, Loader2, Download, KeyRound } from "lucide-react";
const profileFormSchema = z.object({
  firstName: z.string().max(50).optional(),
  lastName: z.string().max(50).optional(),
  email: z.string().email().optional(),
  phone: z.string().max(20).optional(),
  avatar: z.string().optional()
});
const passwordFormSchema = z.object({
  currentPassword: z.string().min(6, {
    message: "Current password is required"
  }),
  newPassword: z.string().min(6, {
    message: "Password must be at least 6 characters"
  }),
  confirmPassword: z.string().min(6, {
    message: "Passwords don't match"
  })
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
});
type ProfileFormValues = z.infer<typeof profileFormSchema>;
type PasswordFormValues = z.infer<typeof passwordFormSchema>;
interface AvatarStyle {
  id: string;
  name: string;
  style_id: string;
  api_version: string;
}
interface Avatar {
  id: string;
  style_id: string;
  seed: string;
  url: string;
  storage_path: string;
}
const ALLOWED_STYLES = ["tapback-memoji", "lorelei"];
const PreferencesPage = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [avatarStyles, setAvatarStyles] = useState<AvatarStyle[]>([]);
  const [selectedStyleId, setSelectedStyleId] = useState<string | null>(null);
  const [availableAvatars, setAvailableAvatars] = useState<Avatar[]>([]);
  const [avatarsByStyle, setAvatarsByStyle] = useState<Record<string, Avatar[]>>({});
  const [loadingAvatars, setLoadingAvatars] = useState(false);
  const [generatingAvatars, setGeneratingAvatars] = useState(false);
  const [showCustomMemojiCreator, setShowCustomMemojiCreator] = useState(false);
  const navigate = useNavigate();
  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      avatar: ""
    }
  });
  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    }
  });
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
          return;
        }
        setUser(session.user);
        const metadata = session.user.user_metadata || {};
        profileForm.reset({
          firstName: metadata.firstName || "",
          lastName: metadata.lastName || "",
          email: session.user.email || "",
          phone: metadata.phone || "",
          avatar: metadata.avatar || ""
        });
      } catch (error) {
        console.error("Error fetching user:", error);
        toast.error("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate, profileForm]);
  useEffect(() => {
    const fetchAvatarStyles = async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('avatar_styles').select('*').in('style_id', ALLOWED_STYLES);
        if (error) throw error;
        if (data && data.length > 0) {
          setAvatarStyles(data as AvatarStyle[]);
          setSelectedStyleId(data[0].id);
          await fetchAllAvatars(data as AvatarStyle[]);
        } else {
          console.log("No allowed styles found, fetching all styles as fallback");
          const {
            data: allData,
            error: allError
          } = await supabase.from('avatar_styles').select('*');
          if (allError) throw allError;
          if (allData && allData.length > 0) {
            const filteredStyles = allData.filter(style => ALLOWED_STYLES.includes(style.style_id.toLowerCase()));
            if (filteredStyles.length > 0) {
              setAvatarStyles(filteredStyles as AvatarStyle[]);
              setSelectedStyleId(filteredStyles[0].id);
              await fetchAllAvatars(filteredStyles as AvatarStyle[]);
            } else {
              setAvatarStyles(allData.slice(0, 2) as AvatarStyle[]);
              setSelectedStyleId(allData[0].id);
              await fetchAllAvatars(allData.slice(0, 2) as AvatarStyle[]);
              console.log("Using first two available styles as fallback");
            }
          }
        }
      } catch (error) {
        console.error("Error fetching avatar styles:", error);
        toast.error("Failed to load avatar styles");
      }
    };
    fetchAvatarStyles();
  }, []);
  const fetchAllAvatars = async (styles: AvatarStyle[]) => {
    if (!styles.length) return;
    try {
      setLoadingAvatars(true);
      const avatarsByStyleTemp: Record<string, Avatar[]> = {};
      let needToGenerateForStyles: AvatarStyle[] = [];

      // First, try to fetch existing avatars for all styles
      for (const style of styles) {
        const {
          data,
          error
        } = await supabase.from('avatars').select('*').eq('style_id', style.id);
        if (error) throw error;
        if (data && data.length > 0) {
          avatarsByStyleTemp[style.id] = data as Avatar[];
        } else {
          needToGenerateForStyles.push(style);
        }
      }

      // Generate avatars for styles that don't have any
      if (needToGenerateForStyles.length > 0) {
        setGeneratingAvatars(true);
        for (const style of needToGenerateForStyles) {
          await generateAvatarsForStyle(style.id);

          // After generation, fetch the new avatars
          const {
            data,
            error
          } = await supabase.from('avatars').select('*').eq('style_id', style.id);
          if (error) throw error;
          if (data && data.length > 0) {
            avatarsByStyleTemp[style.id] = data as Avatar[];
          }
        }
        setGeneratingAvatars(false);
      }
      setAvatarsByStyle(avatarsByStyleTemp);

      // Set the avatars for the initially selected style
      if (selectedStyleId && avatarsByStyleTemp[selectedStyleId]) {
        setAvailableAvatars(avatarsByStyleTemp[selectedStyleId]);
      }
    } catch (error) {
      console.error("Error fetching all avatars:", error);
      toast.error("Failed to load avatars");
    } finally {
      setLoadingAvatars(false);
    }
  };
  useEffect(() => {
    // When selected style changes, update available avatars from our cached collection
    if (selectedStyleId && avatarsByStyle[selectedStyleId]) {
      setAvailableAvatars(avatarsByStyle[selectedStyleId]);
    } else if (selectedStyleId) {
      // If we somehow don't have avatars for this style in our cache, fetch them
      const fetchAvatarsForStyle = async () => {
        try {
          setLoadingAvatars(true);
          const {
            data,
            error
          } = await supabase.from('avatars').select('*').eq('style_id', selectedStyleId);
          if (error) throw error;
          if (data && data.length > 0) {
            setAvailableAvatars(data as Avatar[]);
            // Update our cache
            setAvatarsByStyle(prev => ({
              ...prev,
              [selectedStyleId]: data as Avatar[]
            }));
          } else {
            await generateAvatarsForStyle(selectedStyleId);
          }
        } catch (error) {
          console.error("Error fetching avatars for style:", error);
        } finally {
          setLoadingAvatars(false);
        }
      };
      fetchAvatarsForStyle();
    }
  }, [selectedStyleId]);
  const generateAvatarsForStyle = async (styleId: string) => {
    try {
      setGeneratingAvatars(true);
      const selectedStyle = avatarStyles.find(style => style.id === styleId);
      if (!selectedStyle) {
        toast.error("Selected style not found");
        return;
      }
      const {
        data,
        error
      } = await supabase.functions.invoke('generate-avatars', {
        body: {
          styleId: styleId,
          style: selectedStyle.style_id,
          apiVersion: selectedStyle.api_version,
          count: 30
        }
      });
      if (error) throw error;
      toast.success(`Generated ${data?.count || 0} avatars for ${selectedStyle.name}`);
      const {
        data: avatars,
        error: fetchError
      } = await supabase.from('avatars').select('*').eq('style_id', styleId);
      if (fetchError) throw fetchError;
      if (avatars) {
        setAvailableAvatars(avatars as Avatar[]);
        // Update our cache
        setAvatarsByStyle(prev => ({
          ...prev,
          [styleId]: avatars as Avatar[]
        }));
      }
    } catch (error) {
      console.error("Error generating avatars:", error);
      toast.error("Failed to generate avatars");
    } finally {
      setGeneratingAvatars(false);
    }
  };
  useEffect(() => {
    const setDefaultAvatarIfEmpty = async () => {
      const currentAvatar = profileForm.getValues("avatar");
      if (!currentAvatar) {
        try {
          const defaultStyle = ALLOWED_STYLES[0] || "tapback-memoji";
          const defaultAvatar = defaultStyle === "tapback-memoji" 
            ? `https://www.tapback.co/api/avatar/default.webp`
            : `https://api.dicebear.com/7.x/${defaultStyle}/svg?seed=default`;
          profileForm.setValue("avatar", defaultAvatar);
        } catch (error) {
          console.error("Error setting default avatar:", error);
        }
      }
    };
    if (!loading) {
      setDefaultAvatarIfEmpty();
    }
  }, [loading, profileForm]);
  const onSubmitProfile = async (data: ProfileFormValues) => {
    try {
      setIsSaving(true);
      const {
        error
      } = await supabase.auth.updateUser({
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          avatar: data.avatar
        }
      });
      if (error) throw error;
      setUser(prevUser => {
        if (!prevUser) return null;
        return {
          ...prevUser,
          user_metadata: {
            ...prevUser.user_metadata,
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
            avatar: data.avatar
          }
        };
      });
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };
  const onSubmitPassword = async (data: PasswordFormValues) => {
    try {
      setIsChangingPassword(true);
      const {
        error: signInError
      } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: data.currentPassword
      });
      if (signInError) {
        toast.error("Current password is incorrect");
        return;
      }
      const {
        error
      } = await supabase.auth.updateUser({
        password: data.newPassword
      });
      if (error) throw error;
      toast.success("Password updated successfully");
      passwordForm.reset();
      setShowPasswordFields(false);
    } catch (error: any) {
      console.error("Error updating password:", error);
      if (error.message) {
        toast.error(`Failed to update password: ${error.message}`);
      } else {
        toast.error("Failed to update password. Please try again.");
      }
    } finally {
      setIsChangingPassword(false);
    }
  };
  const handleRandomizeAvatar = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('avatars').select('*').order('created_at', {
        ascending: false
      }).limit(50);
      if (error) throw error;
      if (data && data.length > 0) {
        const randomIndex = Math.floor(Math.random() * data.length);
        const randomAvatar = data[randomIndex] as Avatar;
        profileForm.setValue("avatar", randomAvatar.url);
      } else {
        const randomStyle = ALLOWED_STYLES[Math.floor(Math.random() * ALLOWED_STYLES.length)];
        const randomSeed = Math.random().toString(36).substring(2, 10);
        const fallbackAvatar = randomStyle === "tapback-memoji"
          ? `https://www.tapback.co/api/avatar/${randomSeed}.webp`
          : `https://api.dicebear.com/7.x/${randomStyle}/svg?seed=${randomSeed}`;
        profileForm.setValue("avatar", fallbackAvatar);
      }
    } catch (error) {
      console.error("Error getting random avatar:", error);
      toast.error("Failed to get random avatar");
    }
  };
  const handleSelectStyle = (styleId: string) => {
    setSelectedStyleId(styleId);
  };
  const selectAvatar = (avatar: Avatar) => {
    profileForm.setValue("avatar", avatar.url);
    setAvatarDialogOpen(false);
  };

  const handleCustomMemojiCreated = (imageUrl: string) => {
    profileForm.setValue("avatar", imageUrl);
    setShowCustomMemojiCreator(false);
    setAvatarDialogOpen(false);
    toast.success("Custom Memoji avatar set successfully!");
  };
  const togglePasswordFields = () => {
    setShowPasswordFields(prev => !prev);
  };
  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }
  return <div className="container max-w-4xl py-[17px]">
      <h1 className="text-3xl font-bold mb-6">User Preferences</h1>
      
      <div className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>
              Update your personal information.
            </CardDescription>
          </CardHeader>
          <Form {...profileForm}>
            <form onSubmit={profileForm.handleSubmit(onSubmitProfile)}>
              <CardContent className="space-y-6">
                <div className="flex flex-col items-center sm:flex-row sm:items-start gap-6">
                  <FormField control={profileForm.control} name="avatar" render={({
                  field
                }) => <FormItem className="flex flex-col items-center">
                        <FormLabel className="text-center mb-2">Avatar</FormLabel>
                        <div className="mb-4 relative">
                          <div className="w-24 h-24 rounded-full overflow-hidden bg-muted border flex items-center justify-center">
                            {field.value ? <img src={field.value} alt="User avatar" className="w-full h-full object-cover" onError={() => {
                        console.error("Avatar failed to load, using default");
                        const defaultStyle = ALLOWED_STYLES[0] || "tapback-memoji";
                        const fallbackUrl = defaultStyle === "tapback-memoji" 
                          ? `https://www.tapback.co/api/avatar/default.webp`
                          : `https://api.dicebear.com/7.x/${defaultStyle}/svg?seed=default`;
                        profileForm.setValue("avatar", fallbackUrl);
                      }} /> : <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">
                                No avatar
                              </div>}
                          </div>
                          {/* Removed the redundant refresh button here */}
                        </div>
                        <FormControl>
                          <input type="hidden" {...field} />
                        </FormControl>
                        <div className="flex flex-col gap-2 items-center">
                          <Button type="button" variant="outline" size="sm" onClick={handleRandomizeAvatar} className="mb-2">
                            Random Avatar
                          </Button>
                          
                          <Dialog open={avatarDialogOpen} onOpenChange={setAvatarDialogOpen}>
                            <DialogTrigger asChild>
                              <Button type="button" variant="secondary" size="sm">
                                Choose Avatar
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-4xl max-h-[80vh] flex flex-col">
                              <DialogHeader>
                                <DialogTitle>Choose Your Avatar</DialogTitle>
                                <DialogDescription>
                                  Select a style and pick an avatar you like, or create your own custom Memoji.
                                </DialogDescription>
                              </DialogHeader>
                              
                              {showCustomMemojiCreator ? (
                                <CustomMemojiCreator
                                  onAvatarCreated={handleCustomMemojiCreated}
                                  onCancel={() => setShowCustomMemojiCreator(false)}
                                />
                              ) : (
                                <div className="space-y-6 py-4 flex-1 overflow-hidden flex flex-col">
                                  <div className="flex flex-wrap gap-2 justify-center">
                                    {avatarStyles.map(style => <Button key={style.id} type="button" variant={selectedStyleId === style.id ? "default" : "outline"} size="sm" onClick={() => handleSelectStyle(style.id)} className="flex items-center gap-1">
                                        {selectedStyleId === style.id && <Check className="h-3 w-3" />}
                                        {style.name}
                                      </Button>)}
                                  </div>
                                  
                                  {/* Custom Memoji Creator Button */}
                                  <div className="text-center">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setShowCustomMemojiCreator(true)}
                                      className="border-dashed border-2 hover:border-primary"
                                    >
                                      🎨 Create Custom Memoji
                                    </Button>
                                  </div>

                                  <div className="flex-1 overflow-y-auto min-h-0">
                                    <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 mt-4 max-h-96 overflow-y-auto pr-2">
                                     {loadingAvatars || generatingAvatars ? <div className="col-span-6 sm:col-span-8 flex justify-center py-8">
                                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                                      </div> : availableAvatars.length === 0 ? <div className="col-span-6 sm:col-span-8 flex flex-col items-center gap-4 py-8">
                                        <div className="text-center text-muted-foreground">
                                          No avatars available for this style
                                        </div>
                                        <Button onClick={() => generateAvatarsForStyle(selectedStyleId!)} className="flex items-center gap-2" size="sm">
                                          <Download className="h-4 w-4" />
                                          Generate Avatars
                                        </Button>
                                      </div> : availableAvatars.map(avatar => <div key={avatar.id} className="relative cursor-pointer border rounded-md p-1 hover:bg-muted transition-colors" onClick={() => selectAvatar(avatar)}>
                                          <img src={avatar.url} alt={`Avatar option`} className="w-full aspect-square object-contain" onError={e => {
                                  const target = e.target as HTMLImageElement;
                                  const defaultStyle = ALLOWED_STYLES[0] || "personas";
                                  target.src = `https://api.dicebear.com/7.x/${defaultStyle}/svg?seed=default`;
                                }} />
                                        </div>)}
                                    </div>
                                  </div>
                                  
                                  {availableAvatars.length > 0 && (
                                    <div className="flex justify-center mt-4">
                                      <Button 
                                        onClick={() => generateAvatarsForStyle(selectedStyleId!)} 
                                        variant="outline" 
                                        size="sm"
                                        disabled={loadingAvatars || generatingAvatars}
                                        className="flex items-center gap-2"
                                      >
                                        <Download className="h-4 w-4" />
                                        Generate More Avatars
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                        </div>
                        <FormMessage />
                      </FormItem>} />
                  
                  <div className="w-full space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField control={profileForm.control} name="firstName" render={({
                      field
                    }) => <FormItem>
                            <FormLabel>First Name</FormLabel>
                            <FormControl>
                              <Input placeholder="John" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />

                      <FormField control={profileForm.control} name="lastName" render={({
                      field
                    }) => <FormItem>
                            <FormLabel>Last Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Doe" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                    </div>

                    <FormField control={profileForm.control} name="email" render={({
                    field
                  }) => <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="john.doe@example.com" {...field} disabled />
                          </FormControl>
                          <FormDescription>
                            Your email address cannot be changed as it serves as your username.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>} />

                    <FormField control={profileForm.control} name="phone" render={({
                    field
                  }) => <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <Input type="tel" placeholder="+1 (555) 123-4567" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? "Saving..." : "Update Profile"}
                </Button>
              </CardFooter>
            </form>
          </Form>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
            <CardDescription>
              Update your password to enhance your account security.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showPasswordFields ? <Form {...passwordForm}>
                <form onSubmit={passwordForm.handleSubmit(onSubmitPassword)} className="space-y-4">
                  <FormField control={passwordForm.control} name="currentPassword" render={({
                field
              }) => <FormItem>
                        <FormLabel>Current Password</FormLabel>
                        <FormControl>
                          <Input type="password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={passwordForm.control} name="newPassword" render={({
                field
              }) => <FormItem>
                        <FormLabel>New Password</FormLabel>
                        <FormControl>
                          <Input type="password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={passwordForm.control} name="confirmPassword" render={({
                field
              }) => <FormItem>
                        <FormLabel>Confirm New Password</FormLabel>
                        <FormControl>
                          <Input type="password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <div className="flex gap-2 pt-2">
                    <Button type="submit" disabled={isChangingPassword}>
                      {isChangingPassword ? "Changing Password..." : "Change Password"}
                    </Button>
                    <Button type="button" variant="outline" onClick={togglePasswordFields}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </Form> : <Button onClick={togglePasswordFields} className="flex items-center gap-2">
                <KeyRound className="h-4 w-4" />
                Change Password
              </Button>}
          </CardContent>
        </Card>
      </div>
    </div>;
};
export default PreferencesPage;